/**
 * BS Logo 3D — Three.js spinning logo for BS Hero V2
 * Ported from Claude Design handoff (index.html, final iteration).
 * All geometry/lighting/material parameters match the design exactly.
 */

import * as THREE from 'three';

const LOOP_SECONDS = 12;
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function initLogoFrame(frame) {
  if (frame.dataset.bsLogoInitialized) return;
  frame.dataset.bsLogoInitialized = '1';

  const logoSrc = frame.dataset.bsLogo;
  if (!logoSrc) return;

  // ---------- renderer ----------
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.18;
  renderer.setClearColor(0xffffff, 1);
  frame.appendChild(renderer.domElement);

  // ---------- scene + camera ----------
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xffffff);

  const camera = new THREE.PerspectiveCamera(22, 1, 0.1, 100);
  function getCameraZ() { return window.innerWidth >= 750 ? 10.0 : 10.8; }
  camera.position.set(0, 0, getCameraZ());
  camera.lookAt(0, 0, 0);

  // ---------- studio lighting (final design version) ----------
  const hemi = new THREE.HemisphereLight(0xffffff, 0xc4c4c4, 0.85);
  scene.add(hemi);

  const key = new THREE.DirectionalLight(0xffffff, 0.95);
  key.position.set(-3, 4, 6);
  scene.add(key);

  const fill = new THREE.DirectionalLight(0xffffff, 0.40);
  fill.position.set(4, -1, 4);
  scene.add(fill);

  // Frontal fill — lifts dark interiors without producing back-rim halos
  const front = new THREE.DirectionalLight(0xffffff, 0.55);
  front.position.set(0, 0.5, 10);
  scene.add(front);

  // Bottom kicker — soft self-illumination on lower bevel edge
  const kicker = new THREE.DirectionalLight(0xffffff, 0.22);
  kicker.position.set(0, -2, 5);
  scene.add(kicker);

  const logoGroup = new THREE.Group();
  scene.add(logoGroup);

  // ---------- image → contour → extrusion pipeline ----------
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      buildLogoFromImage(img);
      start = performance.now();
      if (!REDUCED) animate();
      else renderer.render(scene, camera); // single static frame
    } catch (e) {
      console.error('[bs-logo3d]', e);
    }
  };
  img.src = logoSrc;

  function buildLogoFromImage(image) {
    // Pre-pass: dilate logo colors outward into transparent zone (prevents
    // white UV fringe on the bevel where geometry drifts past the alpha boundary).
    const W0 = image.width, H0 = image.height;
    const src = document.createElement('canvas');
    src.width = W0; src.height = H0;
    const sctx = src.getContext('2d');
    sctx.drawImage(image, 0, 0);
    const srcData = sctx.getImageData(0, 0, W0, H0);
    const srcPix = srcData.data;

    let bMask = new Uint8Array(W0 * H0);
    for (let i = 0; i < W0 * H0; i++) {
      bMask[i] = srcPix[i * 4 + 3] > 128 ? 1 : 0;
    }
    const ITER = 5;
    for (let it = 0; it < ITER; it++) {
      const next = new Uint8Array(bMask);
      for (let y = 0; y < H0; y++) {
        for (let x = 0; x < W0; x++) {
          const i = y * W0 + x;
          if (bMask[i]) continue;
          let r = 0, g = 0, b = 0, n = 0;
          if (x > 0      && bMask[i - 1])  { const k = (i - 1) * 4;  r += srcPix[k]; g += srcPix[k+1]; b += srcPix[k+2]; n++; }
          if (x < W0 - 1 && bMask[i + 1])  { const k = (i + 1) * 4;  r += srcPix[k]; g += srcPix[k+1]; b += srcPix[k+2]; n++; }
          if (y > 0      && bMask[i - W0]) { const k = (i - W0) * 4; r += srcPix[k]; g += srcPix[k+1]; b += srcPix[k+2]; n++; }
          if (y < H0 - 1 && bMask[i + W0]) { const k = (i + W0) * 4; r += srcPix[k]; g += srcPix[k+1]; b += srcPix[k+2]; n++; }
          if (n) {
            const k = i * 4;
            srcPix[k]     = r / n;
            srcPix[k + 1] = g / n;
            srcPix[k + 2] = b / n;
            srcPix[k + 3] = 255;
            next[i] = 1;
          }
        }
      }
      bMask = next;
    }
    sctx.putImageData(srcData, 0, 0);

    // Flatten onto white (prevents transparent edge sampling)
    const flat = document.createElement('canvas');
    flat.width = W0; flat.height = H0;
    const fcx = flat.getContext('2d');
    fcx.fillStyle = '#ffffff';
    fcx.fillRect(0, 0, W0, H0);
    fcx.drawImage(src, 0, 0);

    // Rasterize to working resolution — 700px preserves gothic detail
    const TARGET_W = 700;
    const W = TARGET_W;
    const H = Math.round((image.height / image.width) * TARGET_W);
    const cnv = document.createElement('canvas');
    cnv.width = W; cnv.height = H;
    const cx = cnv.getContext('2d');
    cx.drawImage(image, 0, 0, W, H);
    const data = cx.getImageData(0, 0, W, H).data;

    const mask = new Uint8Array(W * H);
    for (let i = 0; i < W * H; i++) {
      mask[i] = data[i * 4 + 3] > 128 ? 1 : 0;
    }

    // Marching squares → unordered line segments (doubled integer coords)
    const segs = [];
    const sample = (i, j) => {
      if (i < 0 || j < 0 || i >= W || j >= H) return 0;
      return mask[j * W + i];
    };

    for (let j = -1; j < H; j++) {
      for (let i = -1; i < W; i++) {
        const tl = sample(i, j),     tr = sample(i + 1, j);
        const br = sample(i + 1, j + 1), bl = sample(i, j + 1);
        const idx = (tl << 3) | (tr << 2) | (br << 1) | bl;
        if (idx === 0 || idx === 15) continue;

        const Tx = 2*i+1, Ty = 2*j;
        const Rx = 2*i+2, Ry = 2*j+1;
        const Bx = 2*i+1, By = 2*j+2;
        const Lx = 2*i,   Ly = 2*j+1;

        switch (idx) {
          case 1:  segs.push([Lx,Ly, Bx,By]); break;
          case 2:  segs.push([Bx,By, Rx,Ry]); break;
          case 3:  segs.push([Lx,Ly, Rx,Ry]); break;
          case 4:  segs.push([Rx,Ry, Tx,Ty]); break;
          case 5:  segs.push([Lx,Ly, Tx,Ty]); segs.push([Rx,Ry, Bx,By]); break;
          case 6:  segs.push([Bx,By, Tx,Ty]); break;
          case 7:  segs.push([Lx,Ly, Tx,Ty]); break;
          case 8:  segs.push([Tx,Ty, Lx,Ly]); break;
          case 9:  segs.push([Tx,Ty, Bx,By]); break;
          case 10: segs.push([Tx,Ty, Rx,Ry]); segs.push([Bx,By, Lx,Ly]); break;
          case 11: segs.push([Tx,Ty, Rx,Ry]); break;
          case 12: segs.push([Rx,Ry, Lx,Ly]); break;
          case 13: segs.push([Rx,Ry, Bx,By]); break;
          case 14: segs.push([Bx,By, Lx,Ly]); break;
        }
      }
    }

    // Chain segments into closed polylines
    const startMap = new Map();
    const kk = (x, y) => (x * 100003 + y) >>> 0;
    for (let s = 0; s < segs.length; s++) {
      const key = kk(segs[s][0], segs[s][1]);
      if (!startMap.has(key)) startMap.set(key, []);
      startMap.get(key).push(s);
    }
    const used = new Uint8Array(segs.length);
    const polys = [];
    for (let s0 = 0; s0 < segs.length; s0++) {
      if (used[s0]) continue;
      const poly = [];
      let cur = s0;
      let guard = 0;
      const startKey = kk(segs[s0][0], segs[s0][1]);
      poly.push([segs[cur][0], segs[cur][1]]);
      while (true) {
        if (used[cur]) break;
        used[cur] = 1;
        const ex = segs[cur][2], ey = segs[cur][3];
        poly.push([ex, ey]);
        const nextKey = kk(ex, ey);
        if (nextKey === startKey) break;
        const candidates = startMap.get(nextKey);
        if (!candidates) break;
        let nxt = -1;
        for (const c of candidates) if (!used[c]) { nxt = c; break; }
        if (nxt < 0) break;
        cur = nxt;
        if (++guard > 200000) break;
      }
      if (poly.length >= 6) polys.push(poly);
    }

    // Convert doubled-pixel coords → world XY (centered, Y-up)
    const TARGET_H_WORLD = 2.55;
    const px2world = TARGET_H_WORLD / H;
    const halfWorldW = (W * px2world) / 2;
    const halfWorldH = (H * px2world) / 2;
    const toWorld = (x, y) => [
      (x / 2) * px2world - halfWorldW,
      halfWorldH - (y / 2) * px2world,
    ];
    const worldPolys = polys.map(p => p.map(([x, y]) => toWorld(x, y)));

    // Douglas–Peucker simplification
    const simplified = worldPolys.map(p => simplify(p, 0.004));

    // Hole assignment (odd nesting depth = hole)
    const polyData = simplified.map(p => ({
      pts: p,
      area: Math.abs(signedArea(p)),
      bbox: computeBbox(p),
    }));
    const depths = polyData.map(_ => 0);
    for (let i = 0; i < polyData.length; i++) {
      const probe = polyData[i].pts[0];
      for (let j = 0; j < polyData.length; j++) {
        if (i === j) continue;
        if (polyData[j].area <= polyData[i].area) continue;
        if (!bboxContains(polyData[j].bbox, probe)) continue;
        if (pointInPolygon(probe, polyData[j].pts)) depths[i]++;
      }
    }
    const outers = [];
    polyData.forEach((p, i) => {
      if (depths[i] % 2 === 0) outers.push({ ...p, idx: i, holes: [] });
    });
    polyData.forEach((p, i) => {
      if (depths[i] % 2 === 1) {
        let bestOuter = -1, bestArea = Infinity;
        for (let o = 0; o < outers.length; o++) {
          const out = outers[o];
          if (out.area <= p.area) continue;
          if (!bboxContains(out.bbox, p.pts[0])) continue;
          if (!pointInPolygon(p.pts[0], out.pts)) continue;
          if (out.area < bestArea) { bestArea = out.area; bestOuter = o; }
        }
        if (bestOuter >= 0) outers[bestOuter].holes.push(p);
      }
    });

    // Build THREE.Shape array
    const shapes = outers.map(o => {
      const sh = new THREE.Shape();
      sh.moveTo(o.pts[0][0], o.pts[0][1]);
      for (let i = 1; i < o.pts.length; i++) sh.lineTo(o.pts[i][0], o.pts[i][1]);
      sh.closePath();
      for (const h of o.holes) {
        const path = new THREE.Path();
        path.moveTo(h.pts[0][0], h.pts[0][1]);
        for (let i = 1; i < h.pts.length; i++) path.lineTo(h.pts[i][0], h.pts[i][1]);
        path.closePath();
        sh.holes.push(path);
      }
      return sh;
    });

    // Custom UV generator — logo PNG maps to front + back faces
    const minX = -halfWorldW, minY = -halfWorldH;
    const spanX = halfWorldW * 2, spanY = halfWorldH * 2;
    const uvMap = (x, y) => new THREE.Vector2((x - minX) / spanX, (y - minY) / spanY);
    const uvGen = {
      generateTopUV(geometry, vertices, ia, ib, ic) {
        return [
          uvMap(vertices[ia*3], vertices[ia*3+1]),
          uvMap(vertices[ib*3], vertices[ib*3+1]),
          uvMap(vertices[ic*3], vertices[ic*3+1]),
        ];
      },
      generateSideWallUV() {
        const z = new THREE.Vector2(0, 0);
        return [z, z, z, z];
      },
    };

    // Extrude (final design: depth 0.232, bevel 0.014)
    const DEPTH = 0.232;
    const BEVEL = 0.014;
    const geom = new THREE.ExtrudeGeometry(shapes, {
      depth: DEPTH,
      bevelEnabled: true,
      bevelThickness: BEVEL,
      bevelSize: BEVEL,
      bevelOffset: 0,
      bevelSegments: 4,
      curveSegments: 12,
      steps: 1,
      UVGenerator: uvGen,
    });
    geom.computeVertexNormals();
    geom.translate(0, 0, -DEPTH / 2); // straddle z=0 for clean rotation

    // Texture for front + back faces
    const tex = new THREE.Texture(flat);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
    tex.minFilter = THREE.LinearMipMapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;

    // Materials (final design values: satin sheen, dark metallic sides)
    const faceMat = new THREE.MeshStandardMaterial({
      map: tex,
      color: 0xffffff,
      roughness: 0.42,
      metalness: 0.18,
    });
    const sideMat = new THREE.MeshStandardMaterial({
      color: 0x141414,
      roughness: 0.62,
      metalness: 0.18,
    });

    // index 0 = front+back, index 1 = sides
    const mesh = new THREE.Mesh(geom, [faceMat, sideMat]);
    logoGroup.add(mesh);
  }

  // ---------- helpers (verbatim from design) ----------
  function signedArea(pts) {
    let a = 0;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      a += (pts[j][0] + pts[i][0]) * (pts[j][1] - pts[i][1]);
    }
    return a * 0.5;
  }
  function computeBbox(pts) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of pts) {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    return [minX, minY, maxX, maxY];
  }
  function bboxContains(b, p) {
    return p[0] >= b[0] && p[0] <= b[2] && p[1] >= b[1] && p[1] <= b[3];
  }
  function pointInPolygon(pt, poly) {
    let inside = false;
    const x = pt[0], y = pt[1];
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i][0], yi = poly[i][1];
      const xj = poly[j][0], yj = poly[j][1];
      const intersect = ((yi > y) !== (yj > y)) &&
        (x < (xj - xi) * (y - yi) / ((yj - yi) || 1e-12) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }
  function simplify(points, tol) {
    if (points.length < 4) return points;
    const sqTol = tol * tol;
    const n = points.length;
    const keep = new Uint8Array(n);
    keep[0] = 1; keep[n - 1] = 1;
    const stack = [[0, n - 1]];
    while (stack.length) {
      const [a, b] = stack.pop();
      let maxD = 0, maxI = -1;
      const ax = points[a][0], ay = points[a][1];
      const bx = points[b][0], by = points[b][1];
      const dx = bx - ax, dy = by - ay;
      const denom = dx * dx + dy * dy || 1e-12;
      for (let i = a + 1; i < b; i++) {
        const px = points[i][0], py = points[i][1];
        const t = ((px - ax) * dx + (py - ay) * dy) / denom;
        const cx = ax + t * dx, cy = ay + t * dy;
        const ddx = px - cx, ddy = py - cy;
        const d2 = ddx * ddx + ddy * ddy;
        if (d2 > maxD) { maxD = d2; maxI = i; }
      }
      if (maxD > sqTol && maxI > 0) {
        keep[maxI] = 1;
        stack.push([a, maxI]);
        stack.push([maxI, b]);
      }
    }
    const out = [];
    for (let i = 0; i < n; i++) if (keep[i]) out.push(points[i]);
    if (out.length > 1) {
      const f = out[0], l = out[out.length - 1];
      if (f[0] === l[0] && f[1] === l[1]) out.pop();
    }
    return out;
  }

  // ---------- render loop ----------
  let start = performance.now();
  let rafId = null;
  let paused = false;

  function animate() {
    if (paused) return;
    const t = ((performance.now() - start) / 1000 % LOOP_SECONDS) / LOOP_SECONDS;
    logoGroup.rotation.y = -Math.PI / 2 + t * Math.PI * 2;
    renderer.render(scene, camera);
    rafId = requestAnimationFrame(animate);
  }

  // Pause when off-screen (same pattern as bs-hero.js video observer)
  const visObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !REDUCED) {
          paused = false;
          start = performance.now() - (logoGroup.rotation.y / (Math.PI * 2)) * LOOP_SECONDS * 1000;
          animate();
        } else {
          paused = true;
          if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
        }
      });
    },
    { rootMargin: '0px', threshold: 0.05 }
  );
  visObserver.observe(frame);

  // ---------- resize ----------
  function fit() {
    var container = frame.parentElement || frame;
    var crect = container.getBoundingClientRect();
    var cs    = getComputedStyle(container);

    // Strip border + padding to get the true content area.
    var innerW = crect.width
      - (parseFloat(cs.borderLeftWidth)  || 0) - (parseFloat(cs.borderRightWidth)  || 0)
      - (parseFloat(cs.paddingLeft)      || 0) - (parseFloat(cs.paddingRight)      || 0);
    var innerH = crect.height
      - (parseFloat(cs.borderTopWidth)   || 0) - (parseFloat(cs.borderBottomWidth) || 0)
      - (parseFloat(cs.paddingTop)       || 0) - (parseFloat(cs.paddingBottom)     || 0);

    var scaleFactor = parseFloat(frame.dataset.logoScale) / 100 || 1.0;
    var size;
    if (window.innerWidth >= 750) {
      // Desktop: cap at 78% of available width so the spinning geometry never
      // clips against the container boundary, then take the shorter side.
      size = Math.round(Math.min(innerW * 0.78, innerH) * scaleFactor) || 300;
    } else {
      // Mobile: logo-block height is auto (content-driven) — use width only.
      size = Math.round(innerW * scaleFactor) || 300;
    }

    // Pin frame to exact square via inline styles (overrides any CSS).
    frame.style.width  = size + 'px';
    frame.style.height = size + 'px';

    // renderer.setSize with updateStyle=true sets canvas.style.width/height
    // directly in px — no CSS percentage stretching possible.
    renderer.setSize(size, size);

    camera.position.z = getCameraZ();
    camera.aspect = 1;
    camera.updateProjectionMatrix();
    if (REDUCED) renderer.render(scene, camera);
  }
  const ro = new ResizeObserver(fit);
  ro.observe(frame.parentElement || frame);
  fit();
}

// ---------- boot ----------
function boot() {
  document.querySelectorAll('[data-bs-logo-frame]').forEach(initLogoFrame);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Shopify section re-render support
document.addEventListener('shopify:section:load', boot);
